import { api, authenticatedApi } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';

export type GoogleHealthReauthorizationInvitation = {
  success: true;
  reauthorizationUrl: string;
  expiresAt: string;
  migrationStatus: 'reauthorization_required';
};

export type GoogleHealthAuthorizationStart = {
  success: true;
  authorizationUrl: string;
  expiresAt: string;
  migrationStatus: string;
};

export type GoogleHealthSyntheticReadProbe = {
  mode: "synthetic_read_probe";
  collectionId: "steps.reconcile";
  pageCount: number;
  dataPointCount: number;
};

/**
 * The invitation URL uses a fragment token. Fragments stay inside the
 * participant browser; this service submits the opaque token in a POST body
 * and immediately navigates to the short-lived Google OAuth URL.
 */
export class GoogleHealthService {
  /**
   * Starts the normal participant flow. The API derives participant and study
   * identity solely from the verified participant web session.
   */
  static startParticipantEnrollmentAuthorization(
    token: string,
  ): Promise<GoogleHealthAuthorizationStart> {
    return authenticatedApi.post(
      API_ENDPOINTS.GOOGLE_HEALTH.ENROLLMENT_AUTHORIZATIONS,
      undefined,
      token,
    );
  }

  static startAuthorization(
    participantId: string,
    studyCode: string,
    token: string,
  ): Promise<GoogleHealthAuthorizationStart> {
    return authenticatedApi.post(
      API_ENDPOINTS.GOOGLE_HEALTH.AUTHORIZATIONS,
      { participantId, studyCode },
      token,
    );
  }

  /**
   * Reauthorize the already-active, server-allowlisted synthetic Google
   * connection with the historical collection scope profile. The server
   * intentionally does not mount this endpoint under the default profile.
   */
  static startSyntheticScopeUpgradeAuthorization(
    participantId: string,
    studyCode: string,
    token: string,
  ): Promise<GoogleHealthAuthorizationStart> {
    return authenticatedApi.post(
      API_ENDPOINTS.GOOGLE_HEALTH.SYNTHETIC_SCOPE_UPGRADES,
      { participantId, studyCode },
      token,
    );
  }

  static createReauthorizationInvitation(
    participantId: string,
    studyCode: string,
    token: string,
  ): Promise<GoogleHealthReauthorizationInvitation> {
    return authenticatedApi.post(
      API_ENDPOINTS.GOOGLE_HEALTH.REAUTHORIZATION_INVITATIONS,
      { participantId, studyCode },
      token,
    );
  }

  static consumeReauthorizationInvitation(
    invitationToken: string,
  ): Promise<GoogleHealthAuthorizationStart> {
    return api.post(
      API_ENDPOINTS.GOOGLE_HEALTH.CONSUME_REAUTHORIZATION_INVITATION,
      { invitationToken },
    );
  }

  /**
   * Explicit, bounded connectivity check for the one server-approved
   * synthetic participant. The API returns counts only and does not persist
   * provider observations.
   */
  static runSyntheticReadProbe(
    participantId: string,
    studyCode: string,
    token: string,
  ): Promise<GoogleHealthSyntheticReadProbe> {
    return authenticatedApi.post(
      API_ENDPOINTS.GOOGLE_HEALTH.READ_PROBE,
      { participantId, studyCode },
      token,
    );
  }
}

export default GoogleHealthService;
